import {
  Box,
  Button,
  CopyTextWithIcon,
  Div,
  H5,
  Img,
  P,
  VerticalSeparator,
  lightTheme,
} from '@vgrubs/components';
import { IntegrationAccessProvider } from '../../types/api/integrationAccess.types';
import { CopyField } from './CopyField';
import { marketplaceLogo } from './helpers/marketplaces';
import { isOtherPos, posDisplayName, posImage } from './helpers/pos';
import { useIntegrationAccess } from './hooks/useIntegrationAccess';
import { useRevealProviderPassword } from './hooks/useRevealProviderPassword';
import { IntegrationStatusChip } from './IntegrationStatusChip';

type Props = {
  locationId: number | string;
};

/**
 * Read-only admin view of the POS + provider access the partner told us they granted. Mounted
 * on Location Details and on Restaurant Verification, inside a modal. Shows, per integration,
 * the access email the partner was asked to add, the grant status, any note, and, for providers
 * that need one (e.g. Grubhub), a button that fetches the password we asked them to set.
 */
export const IntegrationAccessSection = ({ locationId }: Props) => {
  const { data, isLoading, error } = useIntegrationAccess({ locationId });
  const { revealed, reveal, pendingId } = useRevealProviderPassword(locationId);

  const pos = data?.pos ?? null;
  const providers = data?.providers ?? [];
  const isEmpty =
    !data || (data.usesPos === null && !pos && providers.length === 0);

  if (isLoading) {
    return (
      <P $mb="40" $mt="8" $center $color={lightTheme.darkGray}>
        Loading…
      </P>
    );
  }

  if (error) {
    return (
      <P $mb="40" $mt="8" $center $color={lightTheme.darkGray}>
        Could not load the integration access. Close and try again.
      </P>
    );
  }

  if (isEmpty) {
    return (
      <P $mb="40" $mt="8" $center $color={lightTheme.darkGray}>
        No integration access info from the partner yet.
      </P>
    );
  }

  const usesPosLabel =
    data.usesPos === true ? 'Yes' : data.usesPos === false ? 'No' : '-';

  const AccessEmail = ({ email }: { email: string }) => (
    <Div $flex $alignCenter $gap="6px" $wrap>
      <P $m="0" $bold>
        Access email:
      </P>
      <CopyTextWithIcon
        text={email}
        copy={email}
        $m="0"
        color={lightTheme.primary}
      />
    </Div>
  );

  return (
    <Div $mb="40">
      <Box $mb="16" $flex $alignCenter $between>
        <P $m="0" $bold>
          Partner uses a POS system
        </P>
        <P
          $m="0"
          $bold
          $color={
            data.usesPos === true ? lightTheme.green : lightTheme.darkGray
          }
        >
          {usesPosLabel}
        </P>
      </Box>

      {pos && (
        <>
          <P $bold $mb="8" $mt="16">
            POS
          </P>
          <Box $mb="8">
            <Div $flex $alignCenter $between $gap="12px">
              <Div $flex $alignCenter>
                <Img $width="50px" src={posImage(pos.type)} alt="pos" />
                <VerticalSeparator $m="0 14px" $h="34px" />
                <H5 $mb="0">{posDisplayName(pos.type)}</H5>
              </Div>
              <IntegrationStatusChip
                granted={pos.accessGranted}
                grantedAt={pos.accessGrantedAt ?? undefined}
                showPending
              />
            </Div>
            <Div $mt="10">
              {isOtherPos(pos.type) ? (
                <P $m="0" $color={lightTheme.darkGray}>
                  A POS we do not integrate with.
                </P>
              ) : (
                <AccessEmail email={pos.accessEmail} />
              )}
              {pos.note && (
                <P $m="0" $mt="6" $color={lightTheme.darkGray}>
                  Note: {pos.note}
                </P>
              )}
            </Div>
          </Box>
        </>
      )}

      {providers.length > 0 && (
        <>
          <P $bold $mb="8" $mt="16">
            Providers
          </P>
          {providers.map((p) => {
            const logo = marketplaceLogo(p.type, p.name) || p.image;
            return (
              <Box $mb="8" key={p.menuProviderId}>
                <Div $flex $alignCenter $between $gap="12px">
                  <Div $flex $alignCenter>
                    {logo && (
                      <>
                        <Img $width="50px" src={logo} alt="provider" />
                        <VerticalSeparator $m="0 14px" $h="34px" />
                      </>
                    )}
                    <H5 $mb="0">{p.name}</H5>
                  </Div>
                  <IntegrationStatusChip
                    granted={p.accessGranted}
                    grantedAt={p.accessGrantedAt ?? undefined}
                    showPending
                  />
                </Div>
                <Div $mt="10">
                  <AccessEmail email={p.accessEmail} />
                  {p.note && (
                    <P $m="0" $mt="6" $color={lightTheme.darkGray}>
                      Note: {p.note}
                    </P>
                  )}
                </Div>
                {p.hasPassword && (
                  <RevealPassword
                    provider={p}
                    password={revealed[p.menuProviderId]}
                    pending={pendingId === p.menuProviderId}
                    onReveal={() => reveal(p.menuProviderId)}
                  />
                )}
              </Box>
            );
          })}
        </>
      )}
    </Div>
  );
};

/**
 * Masked until asked for. Admin opens this screen long after the partner set the password,
 * often on a shared screen or in a screenshot, so it should not sit in the open. The reveal
 * itself is a server call that lands in the audit log.
 */
const RevealPassword = ({
  provider,
  password,
  pending,
  onReveal,
}: {
  provider: IntegrationAccessProvider;
  password?: string;
  pending: boolean;
  onReveal: () => void;
}) => (
  <Div $mt="12">
    <P $m="0 0 6px 0" $bold $color={lightTheme.primary}>
      The {provider.name} password we set. The partner used it when creating our
      account.
    </P>
    {password ? (
      <CopyField value={password} />
    ) : (
      <Button
        $gray
        $small
        $w="180px"
        $wSM="180px"
        disabled={pending}
        onClick={onReveal}
      >
        {pending ? 'Revealing…' : 'Reveal password'}
      </Button>
    )}
  </Div>
);
